<template>
  <ItemListCollectionWrapper
    :collectionModule="$options.PasswordManagerUserCollection"
    :defaultFilters="defaultFilters"
    :defaultSortBy="defaultSortBy"
    :refreshKey="refreshKey"
    :rowsPerPageLocalStorageKey="$options.rowsPerPageLocalStorageKey"
    :selectable="selectable"
  >
    <template
      v-slot="slotProps"
    >
      <ItemListPagination
        :columns="visibleColumns"
        :currentPageNum="slotProps.currentPageNum"
        :indeterminateSelectAll="slotProps.indeterminateSelectAll"
        :isLoading="slotProps.loading"
        :itemComponent="$options.PasswordManagerUsersRow"
        :rowsPerPage="slotProps.rowsPerPage"
        :selectAll="slotProps.selectAll"
        :selectable="slotProps.selectable"
        :showEmptyState="!slotProps.loading && slotProps.showEmptyState"
        :sortBy="slotProps.sortBy"
        :sortDirection="slotProps.sortDirection"
        :tableData="slotProps.tableData"
        :totalCount="slotProps.totalCount"
        @pageChange="slotProps.handlePageChange"
        @select="slotProps.handleSelect"
        @selectAll="slotProps.handleSelectAll"
        @sortColumn="slotProps.handleSortColumn"
      >
        <template v-slot:toolbar>
          <Toolbar
            :isResendInvitationButtonDisabled="isResendInvitationButtonDisabled"
            :isResendInvitationButtonLoading="isResendInvitationButtonLoading"
            :searchTerm="slotProps.getSearchTerm"
            :shouldFocusSearch="slotProps.shouldFocusSearch"
            :showEnrollButton="showEnrollButton"
            :showRefreshButton="showRefreshButton"
            :showResendInvitationButton="showResendInvitationButton"
            @enrollByUserGroupClick="handleEnrollByUserGroup"
            @refreshTable="handleRefreshTable"
            @resendInvitation="handleResendInvitation"
            @triggerSearch="slotProps.handleEnterSearchTerm"
          />
        </template>
        <template v-slot:emptyState>
          <NoSearchResults
            :src="$options.noResultsIllustration"
            @click="slotProps.clearSearchTerm"
          />
        </template>
      </ItemListPagination>
    </template>
  </ItemListCollectionWrapper>
</template>

<script>
import {
  ItemListPagination,
  NoSearchResults,
} from '@jumpcloud/ui-components';
import { getVisibleColumns } from 'client/PasswordManager/Users/PasswordManagerUsersItemListColumns';
import { mapActions, mapGetters } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import Constants, {
  LocalStorageConsts,
} from 'client/util/Constants';
import ItemListCollectionWrapper from 'client/components/ItemList/ItemListCollectionWrapper.vue';
import PasswordManagerUsersRow from 'client/PasswordManager/Users/PasswordManagerUsersItemListRow.vue';
import Toolbar from 'client/PasswordManager/Users/PasswordManagerUsersItemListToolbar.vue';
import pluralize from 'pluralize';

const PasswordManagerUserCollection = 'PasswordManagerUserCollection';
const { noResultsIllustration } = Constants;

export default {
  name: 'PasswordManagerUsersItemList',

  components: {
    ItemListPagination,
    ItemListCollectionWrapper,
    NoSearchResults,
    Toolbar,
  },

  noResultsIllustration,
  rowsPerPageLocalStorageKey: LocalStorageConsts.passwordManagerUsersRowsPerPage,
  PasswordManagerUsersRow,
  PasswordManagerUserCollection,

  props: {
    defaultFilters: {
      type: Array,
      default: () => [],
    },

    defaultSortBy: {
      type: String,
      default: 'name',
    },

    excludedColumns: {
      type: Array,
      default: () => [],
    },

    selectable: Boolean,

    showEnrollButton: {
      type: Boolean,
      default: true,
    },

    showRefreshButton: {
      type: Boolean,
      default: true,
    },

    showResendInvitationButton: Boolean,
  },

  data() {
    return {
      isResendInvitationButtonLoading: false,
    };
  },

  computed: {
    ...mapGetters(PasswordManagerUserCollection, [
      'refreshKey',
      'getSelectedItemsCount',
      'getSelectedItems',
    ]),

    isResendInvitationButtonDisabled() {
      return this.getSelectedItemsCount === 0;
    },

    visibleColumns() {
      return getVisibleColumns(this.excludedColumns);
    },
  },

  destroyed() {
    this.resetCollection();
  },

  methods: {
    ...mapActions(notificationModule, [
      'triggerErrorNotification',
      'triggerSuccessNotification',
    ]),

    ...mapActions(PasswordManagerUserCollection, [
      'resetCollection',
      'resendInvitation',
      'updateRefreshKey',
    ]),

    handleEnrollByUserGroup() {
      this.$emit('showGroupModal');
    },

    handleRefreshTable() {
      this.updateRefreshKey();
    },

    async handleResendInvitation() {
      this.isResendInvitationButtonLoading = true;
      try {
        await this.resendInvitation(this.getSelectedItems);

        this.triggerSuccessNotification({
          title: 'Success',
          message: `${pluralize('Invitation', this.getSelectedItemsCount)} sent to ${pluralize('user', this.getSelectedItemsCount, true)}.`,
        });
        this.handleRefreshTable();
      } catch (error) {
        this.triggerErrorNotification({
          title: 'Error',
          message: 'Failed to invite users. Please try again later.',
        });
      }

      this.isResendInvitationButtonLoading = false;
    },
  },
};
</script>
